import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { createDispatchers } from "@utils/dispatcher";
import { RootState } from "..";

export type Order = "asc" | "desc";

export interface TableState {
  order: Order;
  orderBy: string;
  selected: (string | number)[];
}

export interface TableInstanceState {
  [tableId: string]: TableState;
}

export interface ITableState {
  tables: TableInstanceState;
}

export const initialTableState: TableState = {
  order: "asc",
  orderBy: "",
  selected: [],
};

export const initialState: ITableState = {
  tables: {},
};

export const tableSlice = createSlice({
  name: "table",
  initialState,
  reducers: {
    // Initialize a table instance with default state
    initializeTable: (
      state,
      action: PayloadAction<{
        tableId: string;
        initialState?: Partial<TableState>;
      }>,
    ) => {
      const { tableId, initialState: customInitialState } = action.payload;
      if (!state.tables[tableId]) {
        state.tables[tableId] = {
          ...initialTableState,
          ...customInitialState,
        };
      }
    },

    // Set sorting order and column
    setSorting: (
      state,
      action: PayloadAction<{ tableId: string; order: Order; orderBy: string }>,
    ) => {
      const { tableId, order, orderBy } = action.payload;
      if (!state.tables[tableId]) {
        state.tables[tableId] = { ...initialTableState };
      }
      state.tables[tableId].order = order;
      state.tables[tableId].orderBy = orderBy;
    },

    // Set selected items
    setSelected: (
      state,
      action: PayloadAction<{ tableId: string; selected: (string | number)[] }>,
    ) => {
      const { tableId, selected } = action.payload;
      if (!state.tables[tableId]) {
        state.tables[tableId] = { ...initialTableState };
      }
      state.tables[tableId].selected = selected;
    },

    // Toggle selection of a single item
    toggleSelection: (
      state,
      action: PayloadAction<{ tableId: string; itemId: string | number }>,
    ) => {
      const { tableId, itemId } = action.payload;
      if (!state.tables[tableId]) {
        state.tables[tableId] = { ...initialTableState };
      }

      const currentSelected = state.tables[tableId].selected;
      const selectedIndex = currentSelected.indexOf(itemId);

      if (selectedIndex === -1) {
        // Add to selection
        state.tables[tableId].selected = [...currentSelected, itemId];
      } else {
        // Remove from selection
        state.tables[tableId].selected = currentSelected.filter(
          (id) => id !== itemId,
        );
      }
    },

    // Select all items
    selectAll: (
      state,
      action: PayloadAction<{ tableId: string; allIds: (string | number)[] }>,
    ) => {
      const { tableId, allIds } = action.payload;
      if (!state.tables[tableId]) {
        state.tables[tableId] = { ...initialTableState };
      }
      state.tables[tableId].selected = [...allIds];
    },

    // Clear all selections
    clearSelection: (state, action: PayloadAction<{ tableId: string }>) => {
      const { tableId } = action.payload;
      if (!state.tables[tableId]) {
        state.tables[tableId] = { ...initialTableState };
      }
      state.tables[tableId].selected = [];
    },

    // Reset table state to initial values
    resetTable: (state, action: PayloadAction<{ tableId: string }>) => {
      const { tableId } = action.payload;
      state.tables[tableId] = { ...initialTableState };
    },

    // Remove table instance (cleanup)
    removeTable: (state, action: PayloadAction<{ tableId: string }>) => {
      const { tableId } = action.payload;
      delete state.tables[tableId];
    },
  },
});

export const {
  initializeTable,
  setSorting,
  setSelected,
  toggleSelection,
  selectAll,
  clearSelection,
  resetTable,
  removeTable,
} = tableSlice.actions;

export const tableDispatcher = createDispatchers(tableSlice.actions);

// Selectors
export const tableSelector = (state: RootState) => state.table;

export const getTableState = (
  state: RootState,
  tableId: string,
): TableState => {
  return state.table.tables[tableId] || initialTableState;
};

export const getTableOrder = (state: RootState, tableId: string): Order => {
  return state.table.tables[tableId]?.order || initialTableState.order;
};

export const getTableOrderBy = (state: RootState, tableId: string): string => {
  return state.table.tables[tableId]?.orderBy || initialTableState.orderBy;
};

export const getTableSelected = (
  state: RootState,
  tableId: string,
): (string | number)[] => {
  return state.table.tables[tableId]?.selected || initialTableState.selected;
};

export const isItemSelected = (
  state: RootState,
  tableId: string,
  itemId: string | number,
): boolean => {
  const selected = getTableSelected(state, tableId);
  return selected.indexOf(itemId) !== -1;
};

export const getSelectedCount = (state: RootState, tableId: string): number => {
  return getTableSelected(state, tableId).length;
};

export const tableReducer = tableSlice.reducer;
